import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab'
import { Avatar, Box, Divider, IconButton, InputAdornment, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Form, useTranslate } from 'ra-core'
import { useState } from 'react'
import {
  CreateBase,
  DateField,
  RecordContextProvider,
  ReferenceManyField,
  TextInput,
  useGetIdentity,
  useGetResourceLabel,
  useListContext,
  useRecordContext,
  useRefresh,
  useSaveContext,
} from 'react-admin'
import { useWatch } from 'react-hook-form'

import { IncidentCategoryField } from './IncidentCategorySelectInput'
import IncidentReportShowCard from './IncidentReportShowCard'
import { IncidentOwnershipField } from './OwnershipSelectInput'
import { IncidentSeverityField } from './SeveritySelectInput'
import { SUMMARY_I18N_PREFIX } from './showShared'
import { getUserFullName } from '../../components/UserFullNameField'
import {
  INCIDENT_REPORT_ACTION_ASSIGN_OWNERSHIP,
  INCIDENT_REPORT_ACTION_UPDATE_SEVERITY,
  INCIDENT_REPORT_REJECTION_REASONS,
} from '../../config/damages'

const TIMELINE_NODE_SIZE = 32
const TIMELINE_NODE_ICON_SIZE = 18
const TIMELINE_CONNECTOR_WIDTH = 2
const EVENT_UI_CONFIG = {
  CREATED: {
    icon: EventOutlinedIcon,
    iconSize: 20,
    color: '#1d4ed8',
    backgroundColor: '#eff6ff',
    borderColor: 'rgba(29, 78, 216, 0.14)',
  },
  CONFIRMED: {
    icon: FactCheckOutlinedIcon,
    iconSize: 20,
    color: '#166534',
    backgroundColor: '#ecfdf5',
    borderColor: 'rgba(22, 101, 52, 0.14)',
  },
  SEVERITY_UPDATED: {
    icon: ReportProblemOutlinedIcon,
    iconSize: 20,
    color: '#d97706',
    backgroundColor: '#fffbeb',
    borderColor: 'rgba(217, 119, 6, 0.14)',
  },
  OWNERSHIP_UPDATED: {
    icon: AssignmentIndOutlinedIcon,
    iconSize: 20,
    color: '#7c3aed',
    backgroundColor: '#f5f3ff',
    borderColor: 'rgba(124, 58, 237, 0.14)',
  },
  RESOLVED: {
    icon: TaskAltRoundedIcon,
    iconSize: 20,
    color: '#166534',
    backgroundColor: '#dcfce7',
    borderColor: 'rgba(22, 101, 52, 0.14)',
  },
  REJECTED: {
    icon: HighlightOffRoundedIcon,
    iconSize: 20,
    color: '#b91c1c',
    backgroundColor: '#fef2f2',
    borderColor: 'rgba(185, 28, 28, 0.14)',
  },
  COMMENT: {
    icon: ChatBubbleOutlineRoundedIcon,
    iconSize: 18,
    color: '#64748b',
    backgroundColor: '#f8fafc',
    borderColor: 'rgba(148, 163, 184, 0.24)',
  },
  DEFAULT: {
    icon: EventOutlinedIcon,
    iconSize: 20,
    color: '#475569',
    backgroundColor: '#f8fafc',
    borderColor: 'rgba(71, 85, 105, 0.12)',
  },
}

const getTimelinePayloadValue = (record) => {
  const payload = record?.details ?? record?.payload ?? record?.data ?? record?.payloads ?? null
  if (payload === null || payload === undefined || payload === '') return null
  if (typeof payload === 'string') return payload
  return JSON.stringify(payload, null, 2)
}

const getDisplayValue = (details, keys = []) => {
  for (const key of keys) {
    if (details?.[key] !== undefined && details?.[key] !== null && details?.[key] !== '') return details[key]
  }
  return null
}

const getTimelineActionTitle = (record, translate, getResourceLabel) => {
  if (record?.type === 'COMMENT') return translate(`${SUMMARY_I18N_PREFIX}.timeline.comment`)

  if (record?.action === INCIDENT_REPORT_ACTION_ASSIGN_OWNERSHIP) {
    return translate(`${SUMMARY_I18N_PREFIX}.timeline.ownershipChanged`)
  }

  if (record?.action === 'UPDATE' || record?.action === INCIDENT_REPORT_ACTION_UPDATE_SEVERITY) {
    if (record?.details?.ownership) {
      return translate(`${SUMMARY_I18N_PREFIX}.timeline.ownershipChanged`)
    }
    if (record?.details?.severity) {
      return translate(`${SUMMARY_I18N_PREFIX}.timeline.severityChanged`)
    }
  }

  return translate(`resources.events.enums.action.values.${String(record?.action || '').toLowerCase()}`, {
    resource: getResourceLabel('incident-reports', 1),
    _: record?.action,
  })
}

const getTimelineVisual = (record) => {
  if (record?.type === 'COMMENT') {
    return EVENT_UI_CONFIG.COMMENT
  }

  if (record?.action === 'UPDATE' || record?.action === INCIDENT_REPORT_ACTION_UPDATE_SEVERITY) {
    if (record?.details?.ownership) {
      return EVENT_UI_CONFIG.OWNERSHIP_UPDATED
    }
    if (record?.details?.severity) {
      return EVENT_UI_CONFIG.SEVERITY_UPDATED
    }
  }

  if (record?.action === INCIDENT_REPORT_ACTION_ASSIGN_OWNERSHIP) {
    return EVENT_UI_CONFIG.OWNERSHIP_UPDATED
  }

  switch (String(record?.action || '').toUpperCase()) {
    case 'CREATE':
      return EVENT_UI_CONFIG.CREATED
    case 'CONFIRM':
      return EVENT_UI_CONFIG.CONFIRMED
    case 'RESOLVE':
      return EVENT_UI_CONFIG.RESOLVED
    case 'REJECT':
      return EVENT_UI_CONFIG.REJECTED
    default:
      return EVENT_UI_CONFIG.DEFAULT
  }
}

const TimelineDetailsRow = ({ value, icon: Icon = null, iconColor = 'text.secondary' }) => {
  if (!value) return null
  const valueNode =
    typeof value === 'string' || typeof value === 'number' ? (
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
        {value}
      </Typography>
    ) : (
      value
    )

  return (
    <Stack direction="row" spacing={0.9} alignItems="center" flexWrap="wrap" useFlexGap>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        {Icon ? <Icon sx={{ fontSize: 18, color: iconColor, flexShrink: 0 }} /> : null}
        {valueNode}
      </Stack>
    </Stack>
  )
}

const ConfirmTimelineDetails = ({ category, severity, ownership }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5, rowGap: 0.75 }}>
    <TimelineDetailsRow value={category ? <IncidentCategoryField value={category} /> : null} />
    <TimelineDetailsRow value={severity ? <IncidentSeverityField value={severity} /> : null} />
    <TimelineDetailsRow value={ownership ? <IncidentOwnershipField value={ownership} /> : null} />
  </Box>
)

const RejectTimelineDetails = ({ details }) => {
  const translate = useTranslate()
  const rejectionReason = getDisplayValue(details, ['rejection_reason', 'rejectionReason'])
  const rejectionLabel = rejectionReason
    ? translate(INCIDENT_REPORT_REJECTION_REASONS[rejectionReason], { _: rejectionReason })
    : null

  return <TimelineDetailsRow value={rejectionLabel} icon={HighlightOffRoundedIcon} iconColor="error.main" />
}

const OwnershipTimelineDetails = ({ ownership }) => (ownership ? <IncidentOwnershipField value={ownership} /> : null)

const SeverityTimelineDetails = ({ severity }) => (severity ? <IncidentSeverityField value={severity} /> : null)

const TimelineActionComment = ({ content }) => {
  return content ? (
    <Typography
      variant="body2"
      sx={{
        color: 'text.primary',
        whiteSpace: 'pre-wrap',
        fontStyle: 'italic',
        lineHeight: 1.55,
      }}
    >
      {content}
    </Typography>
  ) : null
}

const TimelineBody = ({ content, details, isComment, action }) => {
  const translate = useTranslate()
  const payload = getTimelinePayloadValue({ details })
  const isConfirmAction = !isComment && action === 'CONFIRM'
  const isRejectAction = !isComment && action === 'REJECT'
  const isOwnershipUpdate =
    !isComment &&
    (action === INCIDENT_REPORT_ACTION_ASSIGN_OWNERSHIP || (action === 'UPDATE' && Boolean(details?.ownership)))
  const isSeverityUpdate =
    !isComment &&
    (action === 'UPDATE' || action === INCIDENT_REPORT_ACTION_UPDATE_SEVERITY) &&
    Boolean(details?.severity)
  const hasStructuredDetails =
    isConfirmAction || isRejectAction || (!isComment && action === 'RESOLVE') || isOwnershipUpdate || isSeverityUpdate
  const hasBody = Boolean(content || payload || hasStructuredDetails)

  if (!hasBody) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        {translate(`${SUMMARY_I18N_PREFIX}.timeline.noDetails`)}
      </Typography>
    )
  }

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      {isComment ? (
        <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
          {content}
        </Typography>
      ) : null}
      {isConfirmAction ? <ConfirmTimelineDetails {...details} /> : null}
      {isRejectAction ? <RejectTimelineDetails details={details} /> : null}
      {isOwnershipUpdate ? <OwnershipTimelineDetails {...details} /> : null}
      {isSeverityUpdate ? <SeverityTimelineDetails {...details} /> : null}
      {!isComment ? <TimelineActionComment content={content} /> : null}
      {!hasStructuredDetails && payload ? (
        <Box
          component="pre"
          sx={{
            m: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12.5,
            lineHeight: 1.5,
            color: 'text.secondary',
          }}
        >
          {payload}
        </Box>
      ) : null}
    </Stack>
  )
}

const IncidentReportTimelineItem = () => {
  const record = useRecordContext()
  const translate = useTranslate()
  const getResourceLabel = useGetResourceLabel()
  if (!record) return null

  const { details, author_full_name: authorName } = record

  const content = record.content || ''
  const isComment = record.type === 'COMMENT'
  const title = getTimelineActionTitle(record, translate, getResourceLabel)

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 0.9fr) minmax(0, 1.1fr)' },
        gap: { xs: 1, md: 2.25 },
      }}
    >
      <Stack spacing={0.45} sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.35 }}>
          {authorName || translate(`${SUMMARY_I18N_PREFIX}.timeline.unknownAuthor`)}
          {' · '}
          <DateField
            source="created_on"
            showTime
            options={{ year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }}
          />
        </Typography>
      </Stack>
      <Box sx={{ minWidth: 0 }}>
        <TimelineBody content={content} details={details} isComment={isComment} action={record?.action} />
      </Box>
    </Box>
  )
}

const IncidentReportTimelineList = () => {
  const { data, isLoading } = useListContext()
  const translate = useTranslate()

  if (isLoading) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', px: 2, py: 2 }}>
        {translate('ra.message.loading')}
      </Typography>
    )
  }

  if (!data?.length) {
    return (
      <Stack sx={{ px: 2, color: 'text.secondary' }} spacing={0.75} alignItems="flex-start">
        <ScheduleOutlinedIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {translate(`${SUMMARY_I18N_PREFIX}.timeline.emptyTitle`)}
        </Typography>
        <Typography variant="body2" sx={{ maxWidth: 420, lineHeight: 1.5 }}>
          {translate(`${SUMMARY_I18N_PREFIX}.timeline.emptyDescription`)}
        </Typography>
      </Stack>
    )
  }

  return (
    <Timeline sx={{ m: 0, p: 0, '& .MuiTimelineItem-root:before': { display: 'none' } }}>
      {data.toReversed().map((item, index) => {
        const isLast = index === data.length - 1
        const visual = getTimelineVisual(item)
        const DotIcon = visual.icon
        const iconSize = visual.iconSize || TIMELINE_NODE_ICON_SIZE

        return (
          <TimelineItem key={item.id} sx={{ minHeight: 'unset', alignItems: 'stretch' }}>
            <TimelineSeparator sx={{ alignItems: 'center' }}>
              <TimelineConnector
                sx={{
                  bgcolor: index > 0 ? 'divider' : 'transparent',
                  width: TIMELINE_CONNECTOR_WIDTH,
                  borderRadius: 1,
                  height: 14,
                  flexGrow: 0,
                  flexShrink: 0,
                }}
              />
              <TimelineDot
                sx={{
                  m: 0,
                  my: 0.75,
                  p: 0,
                  width: TIMELINE_NODE_SIZE,
                  height: TIMELINE_NODE_SIZE,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: visual.borderColor,
                  backgroundColor: visual.backgroundColor,
                  color: visual.color,
                }}
              >
                <DotIcon sx={{ fontSize: iconSize, display: 'block' }} />
              </TimelineDot>
              {!isLast ? (
                <TimelineConnector
                  sx={{
                    bgcolor: 'divider',
                    width: TIMELINE_CONNECTOR_WIDTH,
                    borderRadius: 1,
                    flexGrow: 1,
                    minHeight: 24,
                  }}
                />
              ) : null}
            </TimelineSeparator>
            <TimelineContent sx={{ py: 0.2, px: 0, pl: 2 }}>
              <RecordContextProvider value={item}>
                <Box sx={{ py: 2.1 }}>
                  <IncidentReportTimelineItem />
                </Box>
              </RecordContextProvider>
              {!isLast ? <Divider /> : null}
            </TimelineContent>
          </TimelineItem>
        )
      })}
    </Timeline>
  )
}

const IncidentReportTimelineComposerFields = () => {
  const translate = useTranslate()
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr)', columnGap: 4, alignItems: 'center' }}>
      <IncidentReportTimelineAvatar />
      <Box sx={{ minWidth: 0 }}>
        <TextInput
          source="content"
          label={false}
          fullWidth
          multiline
          minRows={2}
          maxRows={2}
          helperText={false}
          placeholder={translate(`${SUMMARY_I18N_PREFIX}.timeline.addCommentTitle`)}
          sx={{
            width: '100%',
            m: 0,
            '& .MuiInputBase-root': {
              borderRadius: 1.75,
              pr: 0,
              backgroundColor: (theme) =>
                alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.62 : 0.88),
              alignItems: 'stretch',
              boxShadow: (theme) =>
                `inset 0 0 0 1px ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.9 : 0.5)}`,
            },
            '& .MuiInputBase-inputMultiline': { py: 1.1, pr: 0.5, overflowY: 'hidden', resize: 'none' },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ alignSelf: 'center' }}>
                <IncidentReportTimelineSendButton />
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Box>
  )
}

const IncidentReportTimelineSendButton = () => {
  const translate = useTranslate()
  const { saving } = useSaveContext()
  const content = useWatch({ name: 'content' })
  const canSubmit = Boolean(content?.trim()) && !saving

  return (
    <IconButton
      edge="end"
      type="submit"
      disabled={!canSubmit}
      aria-label={translate(`${SUMMARY_I18N_PREFIX}.timeline.addCommentAction`)}
      sx={{
        pr: 0.4,
        m: 0,
        width: 38,
        height: 38,
        borderRadius: '50%',
        color: canSubmit ? 'primary.main' : 'text.disabled',
        backgroundColor: 'transparent',
        transition: 'color 140ms ease, background-color 140ms ease',
        '&:hover': canSubmit
          ? {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.06),
            }
          : undefined,
      }}
    >
      <SendRoundedIcon />
    </IconButton>
  )
}

const IncidentReportTimelineAvatar = () => {
  const { identity } = useGetIdentity()
  const initials = getUserFullName(identity, { showInitials: true }).trim() || '?'

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Avatar
        sx={{ width: 34, height: 34, fontSize: 12, fontWeight: 700, backgroundColor: '#e2e8f0', color: '#334155' }}
      >
        {initials}
      </Avatar>
    </Box>
  )
}

const IncidentReportTimelineComposerForm = ({ formKey, incidentReportId }) => {
  const { save } = useSaveContext()

  const submit = (values) =>
    save({
      incident_report_id: incidentReportId,
      content: values.content.trim(),
    })

  return (
    <Form key={formKey} defaultValues={{ content: '' }} onSubmit={submit} noValidate>
      <IncidentReportTimelineComposerFields />
    </Form>
  )
}

const IncidentReportTimelineComposer = () => {
  const record = useRecordContext()

  const refresh = useRefresh()
  const [formKey, setFormKey] = useState(0)

  return (
    <Box sx={{ px: { xs: 2, md: 2.25 }, pb: 2 }}>
      <CreateBase
        resource="incident-report-comments"
        redirect={false}
        mutationOptions={{
          onSuccess: () => {
            refresh()
            setFormKey((current) => current + 1)
          },
        }}
      >
        <Box sx={{ pr: { xs: 0, md: 0.25 } }}>
          <IncidentReportTimelineComposerForm formKey={formKey} incidentReportId={record.id} />
        </Box>
      </CreateBase>
    </Box>
  )
}

const IncidentReportTimelineCard = () => {
  const record = useRecordContext()
  return (
    <IncidentReportShowCard titleSuffix="timeline.title" icon={ChatBubbleOutlineRoundedIcon}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, md: 2.25 } }}>
        <ReferenceManyField
          reference="incident-report-comments"
          target="incident_report_id"
          filter={{ incident_report_id: record.id }}
          sort={{ field: 'created_on', order: 'DESC' }}
          perPage={50}
          pagination={false}
        >
          <IncidentReportTimelineList />
        </ReferenceManyField>
      </Box>
      <IncidentReportTimelineComposer />
    </IncidentReportShowCard>
  )
}

export default IncidentReportTimelineCard
